import { useMemo, useEffect, useState } from 'react';

import { formatShortISOAmsterdam, getAmsterdamHours, isTodayAmsterdam } from '@eneco/common/date';
import { useUsageDynamicPricePublicGetDynamicPricesPublic } from '@eneco/dc/hooks';
import type {
  Usages_DynamicPricing_DynamicPricesResponse,
  DC_Domain_Models_Products_ProductType,
  DC_Domain_Models_Products_DenotationType,
  Usages_DynamicPricing_DynamicPrice,
  DC_Domain_Models_Usages_DynamicPricing_DynamicPriceRating,
  DC_Domain_Models_Usages_UsageInterval,
  DC_Domain_Models_Usages_UsageAggregation,
  Usages_DynamicPricing_DynamicPriceVat,
} from '@monorepo-types/dc';

export type TransformedSlice = {
  start: Date;
  isCurrentHour: boolean;
  rating: DC_Domain_Models_Usages_DynamicPricing_DynamicPriceRating;
  price: Usages_DynamicPricing_DynamicPrice;
};

type TransformedData = {
  productType: DC_Domain_Models_Products_ProductType;
  denotationType: DC_Domain_Models_Products_DenotationType;
  slices: TransformedSlice[] | undefined;
  averagePriceVat?: Usages_DynamicPricing_DynamicPriceVat;
}[];

const transformData = (data: Usages_DynamicPricing_DynamicPricesResponse | undefined, now: Date) => {
  if (!data?.products) return undefined;
  return data.products.reduce<TransformedData>((result, product) => {
    const transformedSlices = product.slices
      ?.reduce<TransformedSlice[]>((resultingSlices, slice) => {
        if (slice.price !== null && slice.price !== undefined) {
          const start = new Date(slice.start);
          const isCurrentHour = isTodayAmsterdam(start) && getAmsterdamHours(start) === getAmsterdamHours(now);
          resultingSlices.push({
            isCurrentHour,
            start,
            price: slice.price,
            rating: slice.price.rating,
          });
        }
        return resultingSlices;
      }, [])
      .sort((slice, prevSlice) => (slice.start > prevSlice.start ? 1 : -1));
    result.push({ ...product, slices: transformedSlices?.length ? transformedSlices : undefined });
    return result;
  }, []);
};

export const useDynamicPricing = (
  start: Date,
  interval = 'Hour' as DC_Domain_Models_Usages_UsageInterval,
  aggregation = 'Day' as DC_Domain_Models_Usages_UsageAggregation,
): { isLoading: boolean; hasError: boolean; data: TransformedData | undefined; start: Date | undefined } => {
  const {
    data,
    isLoading: isDataLoading,
    isValidating,
    error,
  } = useUsageDynamicPricePublicGetDynamicPricesPublic({
    start: formatShortISOAmsterdam(start),
    interval,
    aggregation,
  });

  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentDate(new Date());
    }, 60000); // Check time every minute

    return () => clearInterval(intervalId);
  }, []);

  const isLoading = isDataLoading || isValidating;

  const transformedData = useMemo(() => transformData(data, currentDate), [data, currentDate]);
  const hasError = !!error || (!isLoading && transformedData === undefined);
  return { isLoading, hasError, data: transformedData, start: data?.start ? new Date(data.start) : undefined };
};
